import * as React from 'react';
import { Instance } from 'mobx-state-tree';
import styled from '@emotion/styled';
import { inject, observer } from 'mobx-react';
import DutiesSearchForm from './DutiesSearchForm';
import DutiesSearchResult from './DutiesSearchResult';
import MainSectionLayout from '../layout/MainSectionLayout';
import { TLCode } from '../../common/enums';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import ShowDutiesOnWideMap from './ShowDutiesOnWideMap';
import DutiesListActions from './DutiesListActions';
import DutyCalendar from './DutyCalendar';
import { Card, Button } from '@blueprintjs/core';
import { LIST } from '@blueprintjs/icons/lib/esm/generated/iconNames';
import { css } from 'emotion';
import {
  AnchorStore,
  AuthenticationStore,
  DutyAssignmentStore,
  DutyStore,
  DutyTypeStore,
  BlackPointStore,
  DutyOffRequestStore,
  PharmacyDetailedStore,
  PharmacyStore,
  SecretariatNeighborStore,
  UiStore,
  GroupStore,
} from '../../store';
import moment from 'moment';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { generateBasicIcon } from '../../common/icons';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const SearchFormContainer = styled.div`
  flex: 0;
  width: 100%;
`;

const SearchResultContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex: 0;
  width: 100%;
  justify-content: center;
  margin-top: 5px;
`;

const DutiesMapContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const CardStyle = css({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const DivLongLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const DivColumn350px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 350px;
`;

const DivColumn250px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 250px;
`;

const DivColumn175px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 150px;
`;

const DivColumn60 = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 60%;
`;
const DivColumn40 = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 40%;
`;

interface DutiesPageProps {
  DutyAssignmentStore: Instance<typeof DutyAssignmentStore>;
  PharmacyDetailedStore: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore: Instance<typeof PharmacyStore>;
  DutyTypeStore: Instance<typeof DutyTypeStore>;
  DutyStore: Instance<typeof DutyStore>;
  BlackPointStore: Instance<typeof BlackPointStore>;
  DutyOffRequestStore: Instance<typeof DutyOffRequestStore>;
  AnchorStore: Instance<typeof AnchorStore>;
  AuthenticationStore: Instance<typeof AuthenticationStore>;
  SecretariatNeighborStore: Instance<typeof SecretariatNeighborStore>;
  UiStore: Instance<typeof UiStore>;
  GroupStore: Instance<typeof GroupStore>;
}

interface DutiesPageState {
  dutiesGrid: Array<Instance<typeof DutyAssignment>>;
  view: 'grid' | 'calendar' | 'wideMap';
  startDate?: string;
  endDate?: string;
  filter: string;
  isLoading: boolean;
  dutySelected?: Instance<typeof DutyAssignment>;
  minimalDutiesPage: boolean;
  pharmacyId?: number;
}

@inject('AnchorStore')
@inject('AuthenticationStore')
@inject('DutyAssignmentStore')
@inject('SecretariatNeighborStore')
@inject('PharmacyDetailedStore')
@inject('PharmacyStore')
@inject('DutyStore')
@inject('BlackPointStore')
@inject('DutyOffRequestStore')
@inject('DutyTypeStore')
@inject('UiStore')
@inject('GroupStore')
@observer
class DutiesPage extends React.Component<DutiesPageProps & WithTranslateProps, DutiesPageState> {
  constructor(props: DutiesPageProps & WithTranslateProps) {
    super(props);
    this.state = {
      isLoading: true,
      dutiesGrid: [],
      view: 'wideMap',
      filter: '',
      startDate: moment().format(),
      endDate: moment().format(),
      minimalDutiesPage: true,
      pharmacyId: undefined,
    };
  }

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    await Promise.all([
      this.props.DutyTypeStore.checkListLoaded(),
      this.props.PharmacyStore.checkListLoaded(),
      this.props.AnchorStore.checkListLoaded(),
      this.props.GroupStore.checkListLoaded(),
      this.props.SecretariatNeighborStore!.loadList(),
    ]);
    this._isMounted &&
      this.setState({
        isLoading: false,
        minimalDutiesPage: this.isMinimalDutiesPage(),
        pharmacyId: this.getPharmacyId(),
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private isMinimalDutiesPage = (): boolean => {
    if (this.props.AuthenticationStore!.isAdmin || this.props.AuthenticationStore!.isSuperAdmin) return false;
    return true;
  };

  private getPharmacyId = (): number | undefined => {
    if (this.props.AuthenticationStore!.isPharmacy)
      return this.props.AuthenticationStore!.pharmacyId === null
        ? undefined
        : this.props.AuthenticationStore!.pharmacyId;
    return undefined;
  };

  searchDuties = async (criteria: any) => {
    let duties = [];
    await Promise.all([
      this.props.DutyAssignmentStore.searchAssignment(criteria),
      this.props.DutyStore.searchForDutyAssignment(criteria),
    ])
      .catch((e) => console.error(e))
      .then((responses) => (duties = responses[0]));
    return duties;
  };

  searchBlackpoints = async (criteria: any) => {
    let bps = [];
    await Promise.all([this.props.BlackPointStore.search(criteria)])
      .catch((e) => console.error(e))
      .then((responses) => (bps = responses[0]));
    return bps;
  };

  searchDutyOffs = async (criteria: any) => {
    let bps = [];
    await Promise.all([this.props.DutyOffRequestStore.searchDutyOffRequest(criteria)])
      .catch((e) => console.error(e))
      .then((responses) => (bps = responses[0]));
    return bps;
  };

  searchGrid = async (criteria: any) => {
    const duties = await this.searchDuties(criteria);
    this._isMounted && this.setState({ dutiesGrid: duties });
  };

  searchMap = async (criteria: any) => {
    const duties = await this.searchDuties(criteria);
    return duties;
  };

  searchBpts = async (criteria: any) => {
    const bp = await this.searchBlackpoints(criteria);
    return bp;
  };

  searchDtos = async (criteria: any) => {
    const dOff = await this.searchDutyOffs(criteria);
    return dOff;
  };

  onStartDateChange = async (date?: string) => {
    if (date !== this.state.startDate) {
      this._isMounted && this.setState({ startDate: date });
    }
  };

  onMapDateChange = async (date?: string) => {
    this._isMounted && this.setState({ startDate: date, endDate: date });
    this.props.UiStore!.setShowDutiesOnMapDate(date!);
  };

  onEndDateChange = async (date?: string) => {
    if (date !== this.state.endDate) {
      this._isMounted && this.setState({ endDate: date });
    }
  };

  setFilterText = (filter) => {
    this.setState({
      filter,
    });
  };

  onListDutySelect = (selection) => {
    const dutyAss = this.state.dutiesGrid.find((elem) => elem.dutyAssignmentId === selection.id);
    this.setState({ dutySelected: dutyAss });
  };

  render() {
    const { dutiesGrid, view, filter } = this.state;
    const { PharmacyDetailedStore, PharmacyStore, AuthenticationStore, GroupStore } = this.props;
    const pharmacyList = PharmacyStore!.dataList.filter((ph) => ph.isActive);
    const groupList = GroupStore!.dataList;

    return (
      <MainSectionLayout isLoading={this.state.isLoading || pharmacyList.length === 0}>
        <PageContainer>
          {pharmacyList.length !== 0 ? (
            <Card className={CardStyle.toString()}>
              {view === 'grid' ? (
                <>
                  <SearchFormContainer>
                    <DutiesSearchForm
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                      formSubmitted={this.searchGrid}
                      onStartDateChange={this.onStartDateChange}
                      onEndDateChange={this.onEndDateChange}
                      getPharmacyDetails={PharmacyDetailedStore!.loadElement}
                      pharmacyList={pharmacyList}
                      getPharmacyById={PharmacyStore!.getById}
                    />
                  </SearchFormContainer>
                  <SearchResultContainer>
                    <DutiesSearchResult
                      duties={dutiesGrid}
                      setFilterText={this.setFilterText}
                      filter={filter}
                      onListDutySelect={this.onListDutySelect}
                    />
                  </SearchResultContainer>
                  <ButtonContainer>
                    <DutiesListActions
                      onExportToPdf={console.trace}
                      onShowOnMap={() => this.setState({ view: 'wideMap' })}
                    />
                  </ButtonContainer>
                </>
              ) : view === 'wideMap' ? (
                <>
                  <ShowDutiesOnWideMap
                    searchDuties={this.searchMap}
                    searchBlackpoints={this.searchBpts}
                    searchDutyOffs={this.searchDtos}
                    pharmacyList={pharmacyList}
                    getPharmacyById={PharmacyStore!.getById}
                    getPharmacyDetails={PharmacyDetailedStore!.loadElement}
                    date={
                      this.props.UiStore!.showDutiesOnMapDate
                        ? this.props.UiStore!.showDutiesOnMapDate
                        : this.state.startDate!.toString()
                    }
                    anchors={this.props.AnchorStore!.dataList}
                    dutySelected={this.state.dutySelected}
                    onDateChange={this.onMapDateChange}
                    isSecretariat={AuthenticationStore!.isSecretariat || AuthenticationStore!.isAdmin}
                    groupEditMode={false}
                    planningid={this.props.UiStore!.diagnosticPageCurrentPlanningId}
                    groupList={groupList}
                    minimalDutiesPage={this.state.minimalDutiesPage}
                    pharmacyId={this.state.pharmacyId}
                  />
                  <DivLongLine>
                    {this.state.minimalDutiesPage ? (
                      <div></div>
                    ) : (
                      <DivColumn60>
                        <DivLongLine>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde niv.0&nbsp;
                              <img
                                src={generateBasicIcon(0, false, false, false, false).url /*mm20Gray*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde niv.1&nbsp;
                              <img
                                src={generateBasicIcon(1, false, false, false, false).url /*yellow*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde niv.3&nbsp;
                              <img
                                src={generateBasicIcon(3, false, false, false, false).url /*yellowDot*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn250px>
                            <label className="bp3-label .modifier">
                              phie sélect. de garde&nbsp;
                              <img
                                src={generateBasicIcon(3, false, true, false, false).url /*blue*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn250px>
                          <DivColumn250px>
                            <label className="bp3-label .modifier">
                              phie sélect. libre&nbsp;
                              <img
                                src={generateBasicIcon(3, false, false, true, false).url /*lightblue*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn250px>
                          <DivColumn350px>
                            <label className="bp3-label .modifier">
                              garde en zone d'exclusion&nbsp;
                              <img
                                src={generateBasicIcon(1, false, false, false, true).url /*red*/}
                                width="16"
                                height="16"
                              />
                              <img
                                src={generateBasicIcon(3, false, false, false, true).url /*redDot*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn350px>
                        </DivLongLine>
                      </DivColumn60>
                    )}
                    <DivColumn40>
                      {this.state.minimalDutiesPage ? (
                        <div></div>
                      ) : (
                        <ButtonContainer>
                          <Button
                            icon={LIST}
                            onClick={() => this.setState({ view: 'grid' })}
                            text={this.props.translate(TLCode.DutiesShowList)}
                          />
                        </ButtonContainer>
                      )}
                    </DivColumn40>
                  </DivLongLine>
                </>
              ) : (
                <>
                  <DutyCalendar></DutyCalendar>
                </>
              )}
            </Card>
          ) : (
            <Card></Card>
          )}
        </PageContainer>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(DutiesPage);
